import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { ROLES } from '../../constants/roles';
import { DEFAULT_COMPANY_SETTINGS_FORM, mapCompanyToForm } from '../../utils/settingsForm';

export function useCompanySettings() {
  const { t } = useTranslation();
  const { user, company, refreshSession } = useAuth();
  const isManager = user?.role === ROLES.MANAGER;
  const companyId = company?.companyId;

  const [form, setForm] = useState(DEFAULT_COMPANY_SETTINGS_FORM);
  const [initialForm, setInitialForm] = useState(DEFAULT_COMPANY_SETTINGS_FORM);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const load = useCallback(async () => {
    if (!isManager || !companyId) return;
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`/api/companies/${companyId}`);
      const mapped = mapCompanyToForm(res.data);
      setForm(mapped);
      setInitialForm(mapped);
    } catch (err) {
      setError(err.response?.data?.error || t('settings.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [companyId, isManager, t]);

  useEffect(() => {
    load();
  }, [load]);

  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setSuccess(false);
    setError('');
  };

  const setAddressField = (key, value) => {
    setForm((prev) => ({
      ...prev,
      address: { ...prev.address, [key]: value },
    }));
    setSuccess(false);
    setError('');
  };

  const reset = () => {
    setForm(initialForm);
    setError('');
    setSuccess(false);
  };

  const save = async (payload) => {
    if (!companyId) return;
    setSaving(true);
    setError('');
    setSuccess(false);
    try {
      const res = await axios.put(`/api/companies/${companyId}/settings`, payload);
      const mapped = mapCompanyToForm(res.data);
      setForm(mapped);
      setInitialForm(mapped);
      setSuccess(true);
      await refreshSession();
    } catch (err) {
      setError(err.response?.data?.error || t('settings.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  return {
    isManager,
    companyId,
    form,
    setField,
    setAddressField,
    reset,
    save,
    loading,
    saving,
    error,
    success,
    setSuccess,
  };
}

export default useCompanySettings;
